import React, { Fragment, useRef } from "react";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import {
  setProcessing,
  setPanes,
  setFormServerErrors,
  setFormValue,
  loadPage,
  loadEntity,
  createEntity,
  saveEntity,
  removeEntityFromUI,
  deleteEntity,
  disposePage,
  setPageValue,
} from "../../redux/slices/securitygroupspageslice";
import {
  setCurentPage,
  setCurrentPageIsDirty,
  setCurrentPage,
} from "../../redux/slices/currentpageslice";
import { addNotification } from "../../redux/slices/notificationsslice";
import { hideAppDialog, setAppDialog } from "../../redux/slices/appdialogslice";
import { useNavigate, useLocation } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { Page } from "../page";
import { SecurityGroupsPageBody } from "./securitygroupspagebody";
import { formValidator } from "./securitygroupspageformvalidator";
import { deepCompare } from "../../lib/utils";
import { BLOCK_DIRTY_MESSAGE } from "../../settings";

import "./securitygroupspage.css";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const SecurityGroupsPage = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.securityGroupsPage);
  const currentPageState = useSelector((state) => state.currentPage);
  const navigate = useNavigate();

  const { t } = useTranslation();

  let currentPageId = "securityGroupsPage";

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    let { UserssIds, ReportsIds, ...fiv } = pageState.formInitialValues;
    let {
      UserssIds: _UserssIds,
      ReportsIds: _ReportsIds,
      ...fv
    } = pageState.formValues;

    //let isDirty = !deepCompare(pageState.formInitialValues, pageState.formValues);
    let isDirty = !deepCompare(fiv, fv);
    let wasDirty = currentPageState.isDirty;

    if (isDirty != wasDirty) dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*initial page load*/
  useEffectOnce(() => {
    dispatch(loadPage(t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("securitygroups.loading_errormessage"),
                type: "error",
                closable: true,
                duration: 3,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: 3,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("securitygroups.unknown_error"),
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/

  /*toolbar*/
  const handleOnNew = (e) => {
    if (!currentUser.IsAuthenticated) return;

    if (blockNavigationIfDirty() == true) return;

    dispatch(createEntity());
  };

  const handleOnSave = (e) => {
    if (pageState.mode == 1) {
      if (!currentUser.IsAuthenticated) return;
    }

    // ==== 1) Check if Reports View is allowed ====
    const canViewSectionReport = pageState?.formValues?.Claims?.some(
      (f) =>
        f?.Feature === "Reports" &&
        Array.isArray(f.Claims) &&
        f.Claims.some((c) => c.Claim === "View" && c.ClaimValue === true)
    );

    // ==== 2) If not allowed → uncheck all reports BEFORE validation or save ====
    if (!canViewSectionReport) {
      const resetReports = pageState.formValues.Reports.map((r) => ({
        ...r,
        Checked: false,
      }));

      const resetReportsIds = [];

      // set both arrays into Redux
      dispatch(setFormValue({ id: "Reports", value: resetReports }));
      dispatch(setFormValue({ id: "ReportsIds", value: resetReportsIds }));
    }

    // ==== 3) Validate form ====
    const isValidForm =
      formValidator(pageState.mode)(pageState.formValues) === undefined;

    if (!isValidForm) {
      dispatch(
        addNotification({
          text: t("securitygroups.invalid_info"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return;
    }

    // ==== 4) Proceed with save ====
    dispatch(saveEntity(t)).then(
      (response) => {
        dispatch(setCurrentPageIsDirty(false));
      },
      (err) => {
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("securitygroups.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            if (payload.data.ErrorCodes.EntityDoesNotExist) {
              dispatch(removeEntityFromUI(pageState.formValues.Id));
              dispatch(
                addNotification({
                  text: errorMessages[0],
                  type: "error",
                  closable: true,
                  duration: 3,
                  position: "topRight",
                })
              );
            } else {
              dispatch(setFormServerErrors(errorMessages));
            }
          }
        };

        const handleUnhandled = () => {
          dispatch(setFormServerErrors([t("securitygroups.unknown_error")]));
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  /*on user checked/unchecked*/
  const handleOnUserCheck = (item) => {
    let _users = pageState.formValues.Users.map((e) => {
      if (e.Id == item.Id) return { ...e, Checked: !e.Checked };
      else return { ...e };
    });

    let usersIds = [];
    let id = item.Id;
    let isChecked = !item.Checked;

    if (isChecked) usersIds = [...pageState.formValues.UsersIds, id];
    else usersIds = pageState.formValues.UsersIds.filter((e) => e != id);

    dispatch(setFormValue({ id: "Users", value: _users }));
    dispatch(setFormValue({ id: "UsersIds", value: usersIds }));
  };
  /*on user checked/unchecked*/

  /*on user checked/unchecked*/
  const handleOnReportCheck = (item) => {
    let _reports = pageState.formValues.Reports.map((e) => {
      if (e.Id == item.Id) return { ...e, Checked: !e.Checked };
      else return { ...e };
    });

    let reportsIds = [];
    let id = item.Id;
    let isChecked = !item.Checked;

    if (isChecked) reportsIds = [...pageState.formValues.ReportsIds, id];
    else reportsIds = pageState.formValues.ReportsIds.filter((e) => e != id);

    dispatch(setFormValue({ id: "Reports", value: _reports }));
    dispatch(setFormValue({ id: "ReportsIds", value: reportsIds }));
  };

  const handleToggleAllReports = (checked) => {
  const updatedReports = pageState.formValues.Reports.map((r) => ({
    ...r,
    Checked: checked,
  }));

  const updatedIds = checked
    ? updatedReports.map((r) => r.Id)
    : [];

  dispatch(setFormValue({ id: "Reports", value: updatedReports }));
  dispatch(setFormValue({ id: "ReportsIds", value: updatedIds }));
};
  /*on user checked/unchecked*/

  const handleOnErrorbarClose = (e) => {
    dispatch(setFormServerErrors([]));
  };

  /*toolbar*/

  /*delete entity*/
  const handleOnDelete = (id) => {
    //id is zero if deleted from toolbar and > 0 if deleted from entities list
    //if id is zero and mode !=2 => must not happen
    if (!id && pageState.mode != 2) return;

    let isCurrentEntity = false;

    if (!id) {
      //deleted from the toolbar => it's the current entity and mode is 2
      id = pageState.formValues.Id;
      isCurrentEntity = true;
    } else if (pageState.formValues?.Id && pageState.formValues.Id == id)
      isCurrentEntity = true;

    if (isCurrentEntity) {
      //if contains dirty => block
      if (blockNavigationIfDirty() == true) return;
    }

    dispatch(
      setAppDialog({
        isVisible: true,
        className: "securitygroups-page-are-you-sure",

        title: t("securitygroups.warning"),
        content: t("securitygroups.areyousure"),
        actions: {
          yesButton: {
            title: t("securitygroups.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(deleteEntity(id, t)).then(
                (response) => {
                  dispatch(setProcessing({ processing: "" }));
                },
                (err) => {
                  dispatch(setProcessing({ processing: "" }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("securitygroups.unknown_error")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(removeEntityFromUI(id));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: 3,
                        position: "topRight",
                      })
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessage = t("securitygroups.unknown_error");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: 3,
                        position: "topRight",
                      })
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                }
              );
            },
          },
          noButton: { title: t("securitygroups.no") },
        },
      })
    );
  };
  /*delete entity*/

  /*view/edit entity*/
  const handleOnEntitySelected = (entityId, item) => {
    if (blockNavigationIfDirty() == true) return;

    dispatch(loadEntity(entityId, t)).then(
      (response) => {},
      (err) => {
        //hid eloader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("securitygroups.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            if (payload.data.ErrorCodes.EntityDoesNotExist)
              dispatch(removeEntityFromUI(entityId));
          }

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("securitygroups.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };
  /*view/edit entity*/

  const handleOnToolsToggle = (e) => {
    const isVisible = pageState.toolsVisible;

    if (isVisible)
      //hide
      dispatch(
        setPanes([
          {
            size: "0%",
            min: "%",
            max: "%",
            collapsible: false,
          },
          {
            min: "60%",
            max: "80%",
            collapsible: false,
          },
          {
            size: "20%",
            min: "20%",
            max: "20%",
            collapsible: false,
          },
        ])
      );
    else
      dispatch(
        setPanes([
          {
            size: "20%",
            collapsible: false,
          },
          {
            min: "60%",
            max: "60%",
            collapsible: false,
          },
          {
            size: "20%",
            min: "20%",
            max: "20%",
            collapsible: false,
          },
        ])
      );

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
    //dispatch(redrawTiles());
  };

  const handleOnMobileClose = () => {
    dispatch(setPageValue({ id: "mode", value: 0 }));
  };

  const handleToggleClaim = (claimName, actionName, valueForAll) => {
    // Build new Claims array by toggling the exact permission
    const updatedClaims = (pageState?.formValues?.Claims || []).map(
      (groupObj) => {
        if (!groupObj) return groupObj;
        // DTO shape: { Feature: 'X', Claims: [...] }
        if (groupObj.Feature && Array.isArray(groupObj.Claims)) {
          if (groupObj.Feature !== claimName) return groupObj;

          const newClaimsArr = groupObj.Claims.map((p) => {
            const name = p.Claim || p.Action;

            if (valueForAll !== undefined) {
              // If valueForAll is passed, set every action to that value
              return p.hasOwnProperty("ClaimValue")
                ? { ...p, ClaimValue: valueForAll }
                : p.hasOwnProperty("Allowed")
                ? { ...p, Allowed: valueForAll }
                : { ...p, ClaimValue: valueForAll };
            }

            // Toggle single action
            if (name !== actionName) return p;

            if (p.hasOwnProperty("ClaimValue"))
              return { ...p, ClaimValue: !p.ClaimValue };
            if (p.hasOwnProperty("Allowed"))
              return { ...p, Allowed: !p.Allowed };

            return { ...p, ClaimValue: !(p.ClaimValue ?? false) };
          });

          return { ...groupObj, Claims: newClaimsArr };
        }

        // Legacy shape: { Expressions: [...] }
        const key = Object.keys(groupObj)[0];
        if (key) {
          if (key !== claimName) return groupObj;

          const newPerms = (groupObj[key] || []).map((p) => {
            const name = p.Action || p.Claim;

            if (valueForAll !== undefined) {
              return p.hasOwnProperty("Allowed")
                ? { ...p, Allowed: valueForAll }
                : p.hasOwnProperty("ClaimValue")
                ? { ...p, ClaimValue: valueForAll }
                : { ...p, Allowed: valueForAll };
            }

            if (name !== actionName) return p;

            if (p.hasOwnProperty("Allowed"))
              return { ...p, Allowed: !p.Allowed };
            if (p.hasOwnProperty("ClaimValue"))
              return { ...p, ClaimValue: !p.ClaimValue };

            return { ...p, Allowed: !(p.Allowed ?? false) };
          });

          return { [key]: newPerms };
        }

        return groupObj;
      }
    );

    // Update Redux form state
    dispatch(setFormValue({ id: "Claims", value: updatedClaims }));
  };

  const handleSelectAllClaims = (value) => {
    const updatedClaims = (pageState?.formValues?.Claims || []).map(
      (groupObj) => {
        if (!groupObj) return groupObj;

        // Mapped claims
        if (groupObj.Feature && Array.isArray(groupObj.Claims)) {
          const newClaims = groupObj.Claims.map((p) => {
            if (p.hasOwnProperty("ClaimValue"))
              return { ...p, ClaimValue: value };
            if (p.hasOwnProperty("Allowed")) return { ...p, Allowed: value };
            return { ...p, ClaimValue: value };
          });
          return { ...groupObj, Claims: newClaims };
        }

        // Legacy shape
        const key = Object.keys(groupObj)[0];
        if (key) {
          const newPerms = groupObj[key].map((p) => {
            if (p.hasOwnProperty("ClaimValue"))
              return { ...p, ClaimValue: value };
            if (p.hasOwnProperty("Allowed")) return { ...p, Allowed: value };
            return { ...p, Allowed: value };
          });
          return { [key]: newPerms };
        }

        return groupObj;
      }
    );

    dispatch(setFormValue({ id: "Claims", value: updatedClaims }));
  };

  return (
    <Fragment>
      <Page
        id="securitygroupsPage"
        className="securitygroups-page"
        title={t("securitygroups.groups")}
        animation={props.animation}
      >
        <SecurityGroupsPageBody
          onSplitterChange={handleOnSplitterChange}
          onToolsToggle={handleOnToolsToggle}
          onNew={handleOnNew}
          onSave={handleOnSave}
          onDelete={handleOnDelete}
          onErrorbarClose={handleOnErrorbarClose}
          onUserCheck={handleOnUserCheck}
          onReportCheck={handleOnReportCheck}
          onToggleAllReports={handleToggleAllReports}
          onEntitySelected={handleOnEntitySelected}
          onEntityDoubleClick={handleOnEntitySelected}
          updateDirtyState={updateDirtyState}
          onToggleClaim={handleToggleClaim}
          onSelectAllClaims={handleSelectAllClaims}
          onMobileClose={handleOnMobileClose}
        />
      </Page>
      <style>
        {`
                    /*page class*/
                    .securitygroups-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .securitygroups-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .securitygroups-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .securitygroups-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }

                    .securitygroups-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    `}
      </style>
    </Fragment>
  );
};

//export const SecurityGroupsPage = {Component: SecurityGroups};
